import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORY_META } from "../lib/categories";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { BadgeCheck, MapPin, Phone, Mail, Globe, Clock, Sparkles, FileText, Link2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import ContactBusinessDialog from "../components/ContactBusinessDialog";

export default function BusinessDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [b, setB] = useState(null);

  useEffect(() => {
    api.get(`/businesses/${id}`).then((r) => {
      setB(r.data);
      api.post(`/businesses/${id}/track`, { type: "view" }).catch(() => {});
    }).catch(() => nav("/"));
    // engagement on unmount
    const start = Date.now();
    return () => {
      const sec = Math.round((Date.now() - start) / 1000);
      if (sec > 1) api.post(`/businesses/${id}/track`, { type: "engagement", seconds: sec }).catch(() => {});
    };
  }, [id, nav]);

  if (!b) return <div className="mx-auto max-w-5xl px-6 py-24 text-zinc-400 font-mono-tag">Loading…</div>;

  const meta = CATEGORY_META[b.category_slug] || {};
  const Icon = meta.Icon;
  const isSponsored = b.sponsored_until && new Date(b.sponsored_until) > new Date();
  const isVerified = b.verified || b.plan !== "free";
  const isOwner = user && user.id === b.owner_id;

  const seoTitle = b.meta_title || `${b.name} — ${b.category_slug.replace(/-/g, " ")} | BizzAtlas`;
  const seoDesc = b.meta_description || (b.description ? b.description.slice(0, 155) : `${b.name} on BizzAtlas.`);
  const seoImage = b.og_image || b.logo || (b.images || [])[0];
  const businessLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: b.name,
    image: seoImage ? [seoImage] : undefined,
    description: b.description || undefined,
    address: b.address || undefined,
    telephone: b.phone || undefined,
    email: b.email || undefined,
    url: b.website || undefined,
    sameAs: Object.values(b.social_links || {}).filter(Boolean),
    aggregateRating: undefined,
  };

  const handleClick = (kind, value) => {
    api.post(`/businesses/${id}/track`, { type: "click" }).catch(() => {});
    if (kind === "email") window.location.href = `mailto:${value}`;
    if (kind === "phone") window.location.href = `tel:${value}`;
    if (kind === "web")   window.open(value, "_blank", "noreferrer");
  };

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-8 py-12" data-testid="biz-detail-page">
      <Seo
        title={seoTitle}
        description={seoDesc}
        keywords={b.keywords || []}
        ogImage={seoImage}
        canonicalPath={`/business/${b.slug || b.id}`}
        structuredData={businessLd}
      />
      <div className="mb-4">
        <Breadcrumbs
          items={[
            { label: "BizzAtlas", to: "/" },
            { label: "Categories", to: "/categories" },
            { label: b.category_slug.replace(/-/g, " "), to: `/category/${b.category_slug}` },
            { label: b.name },
          ]}
        />
      </div>

      <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
        <div className="flex gap-5">
          {b.logo ? (
            <div className="h-16 w-16 rounded-lg border border-zinc-200 bg-white grid place-items-center overflow-hidden shrink-0">
              <img src={b.logo} alt={`${b.name} logo`} className="h-full w-full object-contain" data-testid="biz-logo-img" />
            </div>
          ) : (
            <div className="h-16 w-16 rounded-lg grid place-items-center text-white shrink-0" style={{ background: meta.accent || "#0F172A" }}>
              {Icon && <Icon className="h-8 w-8" />}
            </div>
          )}
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-4xl tracking-tight">{b.name}</h1>
              {isVerified && <BadgeCheck className="h-6 w-6 text-blue-600" data-testid="biz-verified-badge" />}
              {isSponsored && <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100"><Sparkles className="h-3 w-3 mr-1" />Sponsored</Badge>}
              {b.plan === "premium" && <Badge variant="outline" className="border-blue-200 text-blue-700">Premium</Badge>}
              {b.response_time_label && (
                <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-emerald-50" data-testid="biz-detail-response-time">
                  <Clock className="h-3 w-3 mr-1" />{b.response_time_label}
                </Badge>
              )}
            </div>
            <div className="text-sm text-zinc-500 mt-1 capitalize">{b.category_slug.replace(/-/g, " ")}</div>
          </div>
        </div>
        {isOwner && (
          <Button variant="outline" onClick={() => nav(`/dashboard/listings`)} data-testid="biz-edit-btn">Edit listing</Button>
        )}
      </header>

      {/* Image gallery */}
      {b.images?.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-10">
          {b.images.slice(0, 6).map((url, i) => (
            <div key={i} className="aspect-[4/3] rounded-lg overflow-hidden border border-zinc-200 bg-zinc-100">
              <img src={url} alt={`${b.name} ${i+1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2 space-y-8">
          {b.description && (
            <section>
              <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-2">/ About</div>
              <p className="text-zinc-700 leading-relaxed whitespace-pre-line">{b.description}</p>
            </section>
          )}

          {b.services?.length > 0 && (
            <section>
              <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-3">/ Services</div>
              <div className="flex flex-wrap gap-2">
                {b.services.map((s) => <Badge key={s} variant="outline" className="border-zinc-300 text-zinc-700">{s}</Badge>)}
              </div>
            </section>
          )}

          {b.pricing && (
            <section>
              <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-2">/ Pricing</div>
              <div className="text-zinc-800 whitespace-pre-line">{b.pricing}</div>
            </section>
          )}

          {b.project_links?.length > 0 && (
            <section>
              <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-3">/ Projects</div>
              <div className="space-y-2">
                {b.project_links.map((p, i) => (
                  <a key={i} href={p} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-700 hover:underline text-sm">
                    <Link2 className="h-4 w-4" />{p}
                  </a>
                ))}
              </div>
            </section>
          )}

          {b.documents?.length > 0 && (
            <section>
              <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-3">/ Documents</div>
              <div className="space-y-2">
                {b.documents.map((d, i) => (
                  <a key={i} href={d} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-blue-700 hover:underline text-sm">
                    <FileText className="h-4 w-4" />{d}
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>

        <aside className="space-y-3">
          <div className="rounded-xl border border-zinc-200 p-6 bg-white">
            <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-4">/ Contact</div>
            <div className="mb-4">
              <ContactBusinessDialog business={b} />
            </div>
            <div className="space-y-3 text-sm">
              {b.address && <div className="flex items-start gap-2 text-zinc-700"><MapPin className="h-4 w-4 mt-0.5 text-zinc-400" />{b.address}</div>}
              {b.phone && (
                <button onClick={() => handleClick("phone", b.phone)} className="flex items-center gap-2 text-zinc-700 hover:text-zinc-900" data-testid="biz-phone-btn">
                  <Phone className="h-4 w-4 text-zinc-400" />{b.phone}
                </button>
              )}
              {b.email && (
                <button onClick={() => handleClick("email", b.email)} className="flex items-center gap-2 text-zinc-700 hover:text-zinc-900" data-testid="biz-email-btn">
                  <Mail className="h-4 w-4 text-zinc-400" />{b.email}
                </button>
              )}
              {b.website && (
                <button onClick={() => handleClick("web", b.website)} className="flex items-center gap-2 text-zinc-700 hover:text-zinc-900" data-testid="biz-website-btn">
                  <Globe className="h-4 w-4 text-zinc-400" />{b.website}
                </button>
              )}
              {b.preferred_contact_time && (
                <div className="flex items-center gap-2 text-zinc-700"><Clock className="h-4 w-4 text-zinc-400" />{b.preferred_contact_time}</div>
              )}
            </div>
            {b.social_links && Object.keys(b.social_links).length > 0 && (
              <>
                <Separator className="my-4" />
                <div className="space-y-2 text-sm">
                  {Object.entries(b.social_links).map(([k, v]) => (
                    <a key={k} href={v} target="_blank" rel="noreferrer" className="block text-blue-700 hover:underline">{k}: {v}</a>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="rounded-xl border border-zinc-200 p-5 bg-zinc-50/60 text-xs text-zinc-500 font-mono-tag">
            {b.views} views · {b.clicks} clicks
          </div>
        </aside>
      </div>
    </div>
  );
}
